'use client';

import { cn } from '~/lib/utils';
import { Button } from '../ui/button';
import { useAction } from 'next-safe-action/hooks';
import { notifyInStock } from '~/actions/products/product-actions';
import { toast } from 'sonner';
import {
  NotifyInStockForm,
  type NotifyInStockFormSchema,
} from './product-notify-in-stock-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { useState } from 'react';

export type NotifyInStockProps = {
  isAuthed: boolean;
  itemCode: string;
  isOutOfStock?: boolean;
  isStockRelevant?: boolean;
  className?: string;
};

export const NotifyInStock = ({
  itemCode,
  isOutOfStock,
  isStockRelevant,
  className,
  isAuthed,
}: NotifyInStockProps) => {
  const notifyCodeApi = useAction(notifyInStock);

  const [open, setOpen] = useState(false);

  if (!isOutOfStock || !isStockRelevant) {
    return null;
  }

  const handleSubmit = async (data: NotifyInStockFormSchema | null) => {
    try {
      const res = await toast
        .promise(
          notifyCodeApi.executeAsync({
            itemCode,
            email: data?.email,
            firstName: data?.firstName,
            lastName: data?.lastName,
          }),
          {
            loading: 'Notifying when in stock...',
            success: (response) =>
              `Notification will be sent to ${response?.data?.email}`,
            error: 'Failed to set notification',
          },
        )
        .unwrap();

      return res?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const handleClick = async () => {
    try {
      await handleSubmit(null);
    } catch (err) {
      console.error(err);
    }
  };

  const renderButton = ({ hasModal }: { hasModal: boolean }) => {
    return (
      <Button
        type="button"
        className={cn(
          'inline-flex items-center gap-2 px-4 no-underline hover:underline',
          className,
        )}
        disabled={notifyCodeApi.isExecuting}
        loader={notifyCodeApi.isExecuting}
        variant="link"
        onClick={hasModal ? undefined : handleClick}
      >
        Notify when in stock
      </Button>
    );
  };

  const renderInner = () => {
    if (isAuthed) {
      return renderButton({ hasModal: false });
    }

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {renderButton({ hasModal: true })}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader className="space-y-4">
            {<DialogTitle>Out of Stock</DialogTitle>}
            {
              <DialogDescription>
                Please provide us with your name and email address and we will
                send you an email to let your know when this item is back in
                stock.
              </DialogDescription>
            }
          </DialogHeader>
          <NotifyInStockForm
            onSubmit={async (data) => {
              const res = await handleSubmit(data);
              if (res?.success) {
                setOpen(false);
              }
            }}
            isDisabled={notifyCodeApi.isExecuting}
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="flex h-11 w-full items-center justify-center border-b border-current text-center xl:w-auto">
      {renderInner()}
    </div>
  );
};
